import React, { type ReactNode } from 'react';

/**
 * 2-column form section (Stripe / Vercel / Linear pattern).
 *
 * Title + description sit on the left rail (narrow). Fields sit on the right
 * (wide). On mobile they stack. Sections are separated by a horizontal rule,
 * not a card border, so the page feels like one coherent document rather than
 * a stack of unrelated boxes.
 */
interface FormSectionProps {
  title: ReactNode;
  description?: ReactNode;
  /** Optional inline aside next to the description (e.g. a "Learn more" link). */
  aside?: ReactNode;
  children: ReactNode;
  /** Render a divider above this section (use for all but the first). Default true. */
  divider?: boolean;
}

export const FormSection = ({
  title,
  description,
  aside,
  children,
  divider = true,
}: FormSectionProps): React.JSX.Element => (
  <section
    className={
      'grid gap-6 md:grid-cols-[260px_1fr] md:gap-10 ' +
      (divider ? 'border-t border-zinc-200 pt-8 first:border-t-0 first:pt-0' : '')
    }
  >
    <div className="space-y-1.5">
      <h3 className="text-sm font-semibold tracking-tight text-zinc-900">{title}</h3>
      {description && <p className="text-xs leading-relaxed text-zinc-500">{description}</p>}
      {aside}
    </div>
    <div className="min-w-0 space-y-5">{children}</div>
  </section>
);
